import * as React from 'react';
import {useState, useEffect, useMemo, createContext} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {RunningIndicator} from '../StripeAssistant/LiveAnswersV2/RunningIndicator';

const messages = defineMessages({
  thinking: {
    id: 'conversations_ui.loading_text.thinking',
    description: 'The first loading text that shows',
    defaultMessage: 'Thinking...',
  },
  just_one_moment: {
    id: 'conversations_ui.loading_text.just_one_moment',
    description:
      'The second loading text that shows when the first one is done',
    defaultMessage: 'Just one moment...',
  },
});

type LoadingStage = 'thinking' | 'just_one_moment';

const LoadingTextContext = createContext({
  loadingStage: 'thinking' as LoadingStage,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setLoadingStage: (_: LoadingStage) => {},
});

export function LoadingTextProvider({children}: {children: React.ReactNode}) {
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('thinking');
  const context = useMemo(
    () => ({loadingStage, setLoadingStage}),
    [loadingStage],
  );

  return (
    <LoadingTextContext.Provider value={context}>
      {children}
    </LoadingTextContext.Provider>
  );
}

export function LoadingTextAsync({
  customText,
  onTextChange,
  stage = 'thinking',
}: {
  customText?: string;
  onTextChange?: (text: string) => void;
  stage?: LoadingStage;
}) {
  const intl = useIntl();

  const content = useMemo(() => {
    if (customText) {
      return customText;
    }

    switch (stage) {
      case 'thinking':
        return intl.formatMessage(messages.thinking);
      case 'just_one_moment':
        return intl.formatMessage(messages.just_one_moment);
      default:
        return '';
    }
  }, [intl, stage, customText]);

  useEffect(() => {
    if (onTextChange && content) {
      onTextChange(content);
    }
  }, [content, onTextChange]);

  return <RunningIndicator text={content} testId="loader" />;
}
